import { useState, useCallback, useEffect, useContext, createContext, type ReactNode } from "react";
import {
  Subject,
  BehaviorSubject,
  Observable,
  EMPTY,
  of,
  defer,
  forkJoin,
  combineLatest,
  map,
  switchMap,
  filter,
  startWith,
  take,
  tap,
} from "rxjs";
import { useIntl, defineMessages, type IntlShape } from "react-intl";

import { PageTypes, AnnotationSubtype } from "graphql_globals";
import Env from "config/environment";
import { getAuthorizationHeaders } from "util/http";
import { blobFromUrl, retryBackoffWithCaptureException } from "util/rxjs";
import IconsFontFile from "assets/icons/font/notarize-icons.ttf";
import QuickstampPreviewBackgourndImage from "assets/images/quickstamp_preview.svg";
import { isMobileDevice } from "util/support";
import { useFeatureFlag } from "common/feature_gating";
import { focusForIOs } from "util/html";
import { useProofRebrand } from "util/feature_detection";

import { attachEventListeners } from "./event";
import {
  getPspPageInfo,
  getNotarizeIdFromPspId,
  type KitModule,
  type KitInstance,
  type PageInformation,
  type KitAnnotationWillChangeEvent,
} from "./util";
import {
  expiringNotarizeDecorationContext,
  type DecorationContext,
  type ResizeSettings,
} from "./decoration";
import { checkMarkAttachmentIdFromInstance } from "./annotation/image";
import { makePreviewLocationHook } from "./preview";
import { makeZoomCallback } from "./zoom";
import FrameCSSFile from "./frame.scss?asURL";

type KitConfiguration = Parameters<KitModule["load"]>[0];
type ZoomRef = { current: Exclude<KitConfiguration["initialViewState"], undefined>["zoom"] };
type PSPLoadCallbackRefs = {
  getRenderFn: DecorationContext["getRenderFn"];
  tooltip: Exclude<KitConfiguration["annotationTooltipCallback"], undefined>;
  isEditable: Exclude<KitConfiguration["isEditableAnnotation"], undefined>;
};
type InitialContext = {
  loadInto: (options: LoadOptions) => () => void;
  useTogglePageNavigation: ReturnType<typeof makeTogglePageNavigationHook>;
  usePreviewLocation: ReturnType<typeof makePreviewLocationHook>;
};
type LoadedContextCallbacks = {
  addAnnotation: DecorationContext["addAnnotation"];
  addDesignation: DecorationContext["addDesignation"];
  addIndicator: DecorationContext["addIndicator"];
  setFocused: DecorationContext["setFocused"];
  zoom: ReturnType<typeof makeZoomCallback>;
  jump: ReturnType<typeof makeJumpCallback>;
};
type Context = Readonly<
  | (InitialContext & { [k in keyof LoadedContextCallbacks]?: undefined })
  | (InitialContext & LoadedContextCallbacks)
>;
type JumpAction =
  | { to: "node"; pageIndex: number; pageType: PageTypes; id: string }
  | { to: "page"; pageIndex: number; pageType: PageTypes; point?: { x: number; y: number } };
type ClickInformation = {
  pageType: PageTypes;
  pageIndex: number;
  point: { x: number; y: number };
  shiftKey: boolean;
};
export type PageInfo = { pageIndex: number; totalPages: number };
export type PageSplitInfo = { pageIndex: number; node: HTMLElement };
export type LoadOptions = {
  mainPdfUrl: string;
  container: HTMLElement;
  appendedPdfs?: { pageType: PageTypes; url: string }[];
  onPermissionError?: () => void;
  onPagePress?: (clickInformation: ClickInformation) => void;
  onPageChange?: (pageInfo: PageInfo | null) => void;
  onSelectedDesignationChange?: (designationId: string | null) => void;
  renderPageSplits?: (splits: PageSplitInfo[]) => void;
  readOnly?: boolean;
  onAnnotationWillChange?: (event: KitAnnotationWillChangeEvent) => void;
};

const { apiHost } = Env;
const DOCUMENT_TITLE = "Uploaded PDF Document";
const LICENSE_KEY = getLicenseKey();
const CUSTOM_STYLE_TAG = getInjectedStyleTag();
const LABELS = defineMessages({
  delete: {
    id: "8758af08-ce3d-4335-9bd2-1c0a1184f0c3",
    defaultMessage: "Delete {labelText}annotation",
  },
  reassign: { id: "4d970005-18aa-42fc-97b8-3da3956b4767", defaultMessage: "Reassign" },
  addToGroup: { id: "401ffe3a-2014-4c06-8929-ac189c1d3d4c", defaultMessage: "Add to group" },
  addConditional: {
    id: "2c8f73dd-6ad7-413b-988c-798630be6af7",
    defaultMessage: "Add conditional fields",
  },
  setAsOptional: { id: "bf6ac884-e654-424c-8166-4690b851e5d4", defaultMessage: "Set as optional" },
  decreaseSize: { id: "a0312065-90ae-4386-b8b9-819cd3fd4cdf", defaultMessage: "Decrease size" },
  increaseSize: { id: "0ce9433e-8861-4ed2-97f0-5d5515bd1e57", defaultMessage: "Increase size" },
  edit: { id: "30b20a39-0e4e-4019-b847-2d13101637ac", defaultMessage: "Edit" },
});

const noop = () => {};
const RETRY_CONFIG = { startWait: 100 };
const DEFAULT_CONTEXT: Readonly<InitialContext> = Object.freeze({
  useTogglePageNavigation: () => ({ toggleState: "unknown" as const, toggle: noop }),
  usePreviewLocation: () => null,
  loadInto: () => {
    throw new Error("Unexpected call before PSPDFKit is ready");
  },
});
const requireWrapper = () => {
  throw new Error("Attempted to use this context without the required <PDFWrapper />.");
};
const PDFContext = createContext<Context>(
  Object.freeze({
    useTogglePageNavigation: requireWrapper,
    usePreviewLocation: requireWrapper,
    loadInto: requireWrapper,
    addAnnotation: requireWrapper,
    addDesignation: requireWrapper,
    addIndicator: requireWrapper,
    setFocused: requireWrapper,
    zoom: requireWrapper,
    jump: requireWrapper,
  }),
);
const DEFAULT_INSTANCE_CALLBACKS: Readonly<PSPLoadCallbackRefs> = Object.freeze({
  getRenderFn: () => undefined,
  tooltip: () => [],
  isEditable: () => false,
});
const isMobile = isMobileDevice();

function getInjectedStyleTag(): HTMLStyleElement {
  const tag = document.createElement("style");
  tag.appendChild(
    document.createTextNode(`
@font-face {
  font-family: "notarize-icons";
  font-weight: normal;
  font-style: normal;
  src: url("${IconsFontFile}") format("truetype");
}
[data-annotation-id*="quickstamp-preview|"][data-annotation-id*="indicator|"] {
  background-image: url("${QuickstampPreviewBackgourndImage}");
}
`),
  );
  return tag;
}

function getLicenseKey(): string {
  const { hostname } = window.location;
  return hostname === "localhost"
    ? "Q9WVSpXZkXnonYoFpAd1W7vEE8WnJiudUaAEG1E3AHSWUcAi0wLEoeH_GhNebvF1D4V6Esz-yO20XcMos0-_1_fJIkdfyJdgQ8VTxVdQ3EOJHcD-GUflGUeMQ4XXB_F_7tDOS0oHCJwqRc47fHkxB48W2l1OGKlil8LJEDdjVF5234614hqoCBrIwFg_vG812t_qG2axKXZvnZ-Or4gmdilM95JkdwX-G0f3SuKurwFyWPDw5ckpLYPhEXeeEJA9gJO00SRYB_WzZHtFVxLwojSLPY5UKLTEXfJVK1v5guetUEp2175G-bDIQSKZujjtlcjo2ERh9GRyzgQknOVufCto8xmdmN9o3KVQJs-KLNnILintHKavXEOOGwwbX0n2ngppY3Gfi1U0Fo9oaIBZYMeH_-aWZN65BK6FArR9NeRE8DZRwDj06ATYvjpWr6N_CFzXHhDrncLtV6JTCA463lvXRW6JuQhhsY3gI6DmDfm50WBJqQ7EYVd7WV-i8q5CNXn8CxAktFzP5zdyJSI_ZMJ4h5d0S1mytVywXhyd81sVSLm9yU_rnTii7o1bwVHdaE9C53dis0WrCYeY_idbWYnl6WektdSu6v4DzXa_Ros7NSmQLW2wtw_FKsAr2zLNbXroIGMPfa-0i5lXMnRQFLSOJ5RdqsET8CueFvCd7HE="
    : hostname.endsWith("notarize.rocks")
    ? "aOvxiqtohKtQsNISZpKNOZIIaTPP3ZzXoNm864nm0rIaXIgviDTy0nejovK24sje20DsUZGT-E93l-qMPcFZu_h1C7SDzy2zrQofII37t82qT6dWuFp4airuzMdGGU92egE8wgLeRYWySKOYhRZLjP0gQboNwcQCDGoJzqeryw887y0Ex7Zu7_yhlMXIXXE51F0Qdfb4q1ab8mq2eOp60laDeil_tDphJANqC8kXFh9DI1Al3x_m9w5-LPHuLYrfdD5SQ4B4JLvODtmACFGxW1Q7xTIWQu5PLNkO0VK6AohyDeZ0T5eWVzIZ00_pnRWA5uJOjY1wYD5z6085saUPu4f87prR0gvtPdzFn_hMF21imhj8IjxP320LhnAKqoHnJCbRzT7SP8A6BdlKXVmaeQORKmKPllHwjikXjI72IBX-FCeFhB4dY2rgMD77bFcQZjqg1agHUVNn-bmWEdguRJIQ6oFbwJOr9riILwS_Di5jHG4qcfZB71oTaCKSLZRnTXOqoZdfZmGA4xh94tQ8CdGKlN51M5N6z0MZuKvBk168KLbszl-o7jq5hJTx8t2iFGFas1u_9KRMJNlefKU4mzamq_F5GqP7JvGzIbJ0m76GLZMJO12-rdhmdwb0T-KHa7ryjZhN4g-1EfJpX0gKJ0bvfOuvjAKeG5wMyP3ixAQ="
    : hostname.endsWith("dev-notarize.com")
    ? "rpwdXFdxZiAE4NM5UFZEtGJ_Wl31-eTs4AAPUAj1aSorIhkM3UYre5HG0-LALChmFoLMNYllbpFOvVq9dHF5-YNkcqdmslzKPTk3-mUP9hUJuReIJWB5KplcOylhN_8-EjdkTz-EPS7WAqOrCA08WFUjtdOI0MM7B2Kk_r3b5PygPHUj-ea9MYp9fczLWyBv6smvex9Y7nlfkIu45m-Px4MAmcvFXQRoTSARkBMD4_2iMGUlrfWSZKcDmTxhZ0qEUsvb9uYD_lN4IudiYcQ9Q9ofiqgGPAWtSb548PGJFpfDL7V1sa9K5D_oJMCQMKbkJ3Ow1WkxNA9T_O0Ws0SJpIDiQVEslD_NpdWjarNwStcP8raeIhQhrCsHWZeXd26buXXYCfNgF3y-J4j93XhMZ2_z0MKF2x-uAA0jpjqGaoeJ4Cv4dAg93wWT8gxurdqbEweQNstLJKaARuTWJqK8I4p3lVM3p2Ef8GciZN7L4hz3KmsSlOATahtlIiQivqNK3weAwUxKD07baNRLgdWWV7ZuE5aNUFyQBFUw9W63H8bz65G8lDy6AvJcWSoqQlyqmYv9U4SQnlR-Za0t5h06sU0zIptf5D2TVvjVQUKX_QEP5turmd9w9pl9MySOJ3l2P5-Ll91ofF4y1monuVGu2vsVNr4YkDVaQC3GP8NOzTQ="
    : hostname.endsWith("proof.com")
    ? "jZTkTA3SbtgxacmI5KJBJ_zblwKldSWSltmsV30VDZ7iXS8UwR6VdknjNwt-o5YvmkXKi4AtywXfSg2L7zWUnt4-QuW56PyUfZzyLsm_kA3T9jqqERFXiGxpeolFVmYip6ei8Z-QohwCcJt-M6scrrX4AlCWRl9Gf3xth3lHPmuxN_vHUOwgYseaiL-KOXalCc2eK52y5Ht3jH1qh5yujZhmNqgJCnFP4rm6ne2iJHYnrEfqceVG6tO-v2F905e5eFfBqaQBx5qV2YZE7PogfZZLw_l_alKyjm088T8b5-aqQKTkz7zpLy3xrMHU9xq8-DmvKI8A20JiHWHg_7Iei9QWi6EToDnNa8K6VrhT0al5v8uXSy11Gc65c5D1x8-HY6KjeGOgiXou5ldxLN3AmaLvmjjQZjLIwecZxQTEvYTgFlkxAq5-8oHID6OwsqGqukXabi_SMKvghGDr04a4-I44wkaj-fTX46LKbjWCUPmVUDbNOeuLVlG9RA69osU_dYXqR5HGujAgtDJlXYC4ysrqX7Sxu6tTKZ1czVrk04cJF8st1rGyOs86PoqjwPzrNyqAli7q-FylT2ipu4RcNO30wFOmRE2E0fzdUaYWrQm1rri0WLVhuvgFzpD6BfWOzQe2ipJEovxrN7ha8pXQug=="
    : hostname.endsWith("proof.coach")
    ? "g1B7440VJ9CaymwVM_YJYroRhTb4a6-lNwxhVcnA_xJWFQjfpP3aEspQNn8Qrn0Aa9QlAfDNOPKUGv44R0Mvo1F4PcHtE92CsDMdOthK123uwNbADgmkKLmz3gWdQaVY1fh_sqc4TbA3fHPi-Q6XOY7ZjX1lbrqEDodiueNBZSI2cZoVLJrVLznHSD3epSKFjn8whokb44-J6TrvA0WKULrgWh9s5DNRE1INoOnXQKx6XMPVo7kuccnK5mco4bZWAtNaRqh88w_q_WnMzZjbVe9vPAsjQ6BsVE6vxXZmYtwYBCyW8v3lRYMsw6_LrwrbIiaiB5A8FO7xRumHlJbVVgHrGYhunMLfxT97ijPh2vORUnB8pNCQB-nBu-Pa_I5MTDpP9t2sFc6qwIJ6HSC1rT00SyCBpGH42dMFf9S2KceYvCw045nfBngiV6DCWrsXv_gCtt4YDssJp29ha9EABP6EJ3C5XQJMXwZxJ9foLBFYxl9Z3uA4RG2h-t2_xJWmEDx8NEfhbCcz_2RbsBO2neNkZcY167nP01bSqs8Mh-wut9B5MhlB305vSBaUie22-BeDkZ5bBjpgQXvaAwtjEhoA0Sf-axVp0D_QaxxmJwESwRU-Q4kOJY-INBl_SnEG3fV2hcduvzMl_vU5brzg62rRaQk9dKqGotPv-HmscY8="
    : "P6A0omEdtjWpKasXu5_z-YpTbyTg646xp_eXsQeK5Ad2zij1cnFxofE5Outukm5C5JoTx7KSCBMHNO9S0mAFVR7ADSxetZ2FLHDROt-9d8nn3MhOzw3iUM4bEZaOJX7jtz54AGlo-v8BbPH9_uDyTi9ywp7TrInKjdYzQUDluAu8wUurK0JEgfH8sUA_wFm5T4TKRbX9Z3muoVrz02CVJdR1UV9QzRAoNx372hc893V-hurMBW_ekZMqD3F0jWru2OBRaoqYyCgbVZAv5YZO2Y5nSe6GJj2knnWQLjRccSnkzP6IZERKrIPFkj9NWiBvDfh74M3tjqac7TRnXjt23NsXNH4ZHcLoaz5Jsg_ytRykWxRwxIEI0fCW7MquxvKvoC3EpqdaPvPI7tZ7Vjh0dawidR1fuEdl99yAEoKF6dE95U_NrBquWqBpaEk7VieO5JyHt7LR1auLd5ucVhfW76axlQYEt5hIIRLmmcr8-lJFsT-fYbQ_A5S7ZaSPGAy2UEjQOjVwqNj8T7iK1US2PXh6tJkiUF2CUqvyhhZ0NKcTLc52OqBekAClk4KpyS8KqFCVUscSoO_MO5jWtL1AUB8ENi_5fpkeGn0UBDrCgqLmreG32vdmXhNGPEcMB0mP4Qhb-thQpYI7Z99LUWG9rw==";
}

function makeTogglePageNavigationHook(
  sidebarMode: KitModule["SidebarMode"],
  instance?: KitInstance,
) {
  return function useTogglePageNavigation() {
    const [toggleState, setToggleState] = useState<"unknown" | "open" | "closed">(
      !instance ? "unknown" : instance.viewState.sidebarMode ? "open" : "closed",
    );
    useEffect(() => {
      setToggleState(!instance ? "unknown" : instance.viewState.sidebarMode ? "open" : "closed");
    }, [instance]);
    return {
      toggleState,
      toggle: useCallback(() => {
        if (instance) {
          instance.setViewState((oldState) =>
            oldState.set("sidebarMode", oldState.sidebarMode ? null : sidebarMode.THUMBNAILS),
          );
          setToggleState(instance.viewState.sidebarMode ? "open" : "closed");
        }
      }, [instance]),
    };
  };
}

function makeJumpCallback(
  module: KitModule,
  instance: KitInstance,
  pageInformationLookup: PageInformation,
) {
  return async (action: JumpAction) => {
    const realIndex = pageInformationLookup.getBasisPageIndex(action.pageType) + action.pageIndex;

    switch (action.to) {
      case "page": {
        const point = action.point
          ? {
              top: getPspPageInfo(instance, realIndex).height - action.point.y,
              left: action.point.x,
              width: 1,
              height: 1,
            }
          : undefined;
        return instance.jumpToRect(realIndex, new module.Geometry.Rect(point));
      }
      case "node": {
        const pageAnnotations = await instance.getAnnotations(realIndex);
        const annotation = pageAnnotations.find(
          (annotation) => getNotarizeIdFromPspId(annotation.id) === action.id,
        );
        return annotation && instance.jumpToRect(realIndex, annotation.boundingBox);
      }
    }
  };
}

function useLazyPSPDFKit() {
  const [module, setModule] = useState<null | KitModule>(null);
  useEffect(() => {
    import("pspdfkit").then((mod) => setModule(mod.default));
  }, []);
  return module;
}

function semanticallyHideKitAnnotation(annotationEl?: HTMLElement) {
  annotationEl?.setAttribute("aria-label", "");
  annotationEl?.setAttribute("aria-hidden", "true");
  annotationEl?.setAttribute("tabindex", "-1");
}

function getPDFBytes(pdfUrl: LoadOptions["mainPdfUrl"]): Observable<ArrayBuffer | null> {
  return defer(() => {
    const options = pdfUrl.startsWith(apiHost)
      ? { credentials: "include" as const, headers: getAuthorizationHeaders() }
      : undefined;
    return fetch(pdfUrl, options).then((res) => {
      if (res.ok) {
        return res.arrayBuffer();
      } else if (res.status === 403) {
        // This often means (for s3 links) that they have an expired URL/token. Not all documents come from s3
        // but most do so we give up on this kind of error.
        return null;
      }
      throw new Error(`Error in pdf request: ${pdfUrl}`);
    });
  }).pipe(retryBackoffWithCaptureException(RETRY_CONFIG));
}

function instanceFromOptions(
  module: KitModule,
  pdfBytes: ArrayBuffer,
  { container, readOnly }: LoadOptions,
  containerIsReady$: BehaviorSubject<boolean>,
  zoomRef: ZoomRef,
  isProof: boolean,
) {
  return new Observable<{ instance: KitInstance; instanceCallbacks: PSPLoadCallbackRefs }>(
    (observer) => {
      let localInstance: undefined | KitInstance;
      let done = false;
      const instanceCallbacks = { ...DEFAULT_INSTANCE_CALLBACKS };
      containerIsReady$.next(false);
      const unload = () => {
        module.unload(container);
        containerIsReady$.next(true);
      };
      const customRenderAnnotationCache = new Map<string, HTMLDivElement>();
      module
        .load({
          container,
          // Without manually specifiying this baseUrl, PSPDFKit seems to have a Firefox bug with auto detection and
          // internal chunk loading fails.
          baseUrl: `${window.location.origin}/`,
          customRenderers: {
            Annotation: ({ annotation }) => {
              const renderFn = instanceCallbacks.getRenderFn(annotation);
              if (renderFn) {
                let node = customRenderAnnotationCache.get(annotation.id);
                if (node) {
                  // clear our aria default label
                  const annotationEl = node.parentElement?.parentElement?.querySelector(
                    `[data-annotation-id='${annotation.id}']`,
                  );
                  semanticallyHideKitAnnotation(annotationEl as HTMLElement | undefined);
                }
                if (!node) {
                  node = window.document.createElement("div");
                  node.classList.add("Notarize-Custom-Annotation-Container");
                  const renderObserver = new MutationObserver((records) => {
                    // We want to hide the regular buttton for screen readers so that there's only one button for
                    // these users to interact with.
                    const neighborButton = records[0]?.target.parentElement?.previousElementSibling;
                    if (neighborButton) {
                      const annotationEl = neighborButton.firstChild;
                      semanticallyHideKitAnnotation(annotationEl as HTMLElement | undefined);
                      renderObserver.disconnect();
                    }
                  });

                  renderObserver.observe(node, { childList: true });
                  customRenderAnnotationCache.set(annotation.id, node);
                }
                renderFn(node);
                return {
                  node,
                  noZoom: false,
                  append: true,
                  onDisappear: noop,
                };
              }
              return null;
            },
          },
          disableMultiSelection: true,
          document: pdfBytes,
          licenseKey: LICENSE_KEY,
          // IMPORTANT NOTE: Its crucial that these functions return falsy for annotations created outside of Notarize.
          // Things like form fields must be "read only" and non-Notarize annotations cannot be moved since we have no
          // way to persist changes. We don't want users to use Notarize to make changes to these.
          isEditableAnnotation: (pspAnnotation) =>
            !readOnly && instanceCallbacks.isEditable(pspAnnotation),
          annotationTooltipCallback: (pspAnnotation) =>
            readOnly ? [] : instanceCallbacks.tooltip(pspAnnotation),
          styleSheets: [FrameCSSFile],
          initialViewState: new module.ViewState({
            allowPrinting: false,
            allowExport: false,
            zoom: zoomRef.current,
            showComments: false,
            showToolbar: false,
            enableAnnotationToolbar: false,
            showAnnotationNotes: false,
            // @ts-expect-error -- pspdfkit wants an immutable object that we can't construct
            viewportPadding: { horizontal: 16, vertical: 16 },
            readOnly,
          }),
        })
        .then((instance) => {
          if (done) {
            unload();
          } else {
            // We do not call observer.complete() to keep the instance "live" and only unload() when downstream unsubscribe().
            localInstance = instance;
            observer.next({ instance, instanceCallbacks });
            // We include this inline style tag to include the inline data/bas64 for the icons font.
            const { contentDocument, contentWindow } = instance;
            contentDocument.head.appendChild(CUSTOM_STYLE_TAG);
            contentWindow.frameElement?.setAttribute("title", DOCUMENT_TITLE);
            contentWindow.document.body.setAttribute(
              "data-brand-colors",
              isProof ? "proof" : "notarize",
            );
            contentDocument.title = DOCUMENT_TITLE;
          }
        })
        .catch((e) => {
          unload();
          observer.error(e);
        });
      return () => {
        done = true;
        if (localInstance) {
          unload();
        }
      };
    },
  ).pipe(retryBackoffWithCaptureException(RETRY_CONFIG));
}

const SPREAD_SPACING = 32;
function preventPageSpreadScale(instance: KitInstance) {
  instance.setViewState((viewState) =>
    viewState.set("spreadSpacing", SPREAD_SPACING / instance.currentZoomLevel),
  );
}

function renderCustomOverlays(
  module: KitModule,
  instance: KitInstance,
  { renderPageSplits }: LoadOptions,
) {
  if (renderPageSplits) {
    preventPageSpreadScale(instance);

    const splits = [...Array(instance.totalPageCount - 1).keys()].map((i) => {
      const pageIndex = i + 1;
      const split = document.createElement("div");
      split.classList.add("PageSplitContainer");
      split.style.marginTop = `-${SPREAD_SPACING / 2}px`;

      const item = new module.CustomOverlayItem({
        id: `page-split-${pageIndex}`,
        node: split,
        pageIndex,
        position: new module.Geometry.Point({
          x: instance.pageInfoForIndex(pageIndex)!.width / 2,
          y: 0,
        }),
        onAppear: () => {
          // ignore overflow: hidden from page
          (split.offsetParent as HTMLElement).style.position = "fixed";
        },
        disableAutoZoom: true,
      });

      instance.setCustomOverlayItem(item);

      return { pageIndex, node: split };
    });

    instance.addEventListener("viewState.change", () => {
      preventPageSpreadScale(instance);
    });

    renderPageSplits(splits);
  }
}

function appendedPdfsFromInstance(
  instance: KitInstance,
  { appendedPdfs }: LoadOptions,
): Observable<PageInformation> {
  if (!appendedPdfs?.length) {
    return of({
      getBasisPageIndex: () => 0,
      getNormalizedPageInfo: (index) => ({ normalizedIndex: index, pageType: PageTypes.DOCUMENT }),
    });
  }
  const appendedPdfsConfigs$ = appendedPdfs.map(({ url, pageType }) => {
    return blobFromUrl(url).pipe(
      retryBackoffWithCaptureException(RETRY_CONFIG),
      map((blob) => ({ blob, pageType })),
    );
  });
  return forkJoin(appendedPdfsConfigs$).pipe(
    switchMap((appendedPdfsConfigs) => {
      const totalPagesBeforeAppending = instance.totalPageCount;
      const operations = appendedPdfsConfigs.map(({ blob }, index) => ({
        type: "importDocument" as const,
        treatImportedDocumentAsOnePage: false,
        afterPageIndex: index + totalPagesBeforeAppending - 1,
        document: blob,
      }));
      const applyOperations$ = defer(() => instance.applyOperations(operations));
      return applyOperations$.pipe(
        map(() => {
          // WARNING: This supposes 1 page appended pdfs (like loose leafs)
          const getBasisPageIndex = (pageType: PageTypes) => {
            if (pageType === PageTypes.DOCUMENT) {
              return 0;
            }
            const foundIndex = appendedPdfs.findIndex((pdf) => pdf.pageType === pageType);
            return foundIndex === -1 ? 0 : foundIndex + totalPagesBeforeAppending;
          };
          return {
            getBasisPageIndex,
            getNormalizedPageInfo: (index: number) => {
              if (index < totalPagesBeforeAppending) {
                return { normalizedIndex: index, pageType: PageTypes.DOCUMENT };
              }
              const diff = index - totalPagesBeforeAppending;
              if (diff >= appendedPdfs.length) {
                throw new Error(
                  `Could not find ${index} with ${appendedPdfs.length} docs and total ${totalPagesBeforeAppending}`,
                );
              }
              const found = appendedPdfs[diff];
              return { normalizedIndex: 0, pageType: found.pageType };
            },
          };
        }),
      );
    }),
  );
}

function fullyLoadedContextFromOptions(
  module: KitModule,
  options: LoadOptions,
  loadInto: Context["loadInto"],
  containerIsReady$: BehaviorSubject<boolean>,
  zoomRef: ZoomRef,
  mwebAnnotationResizingSettings: ResizeSettings | null,
  isProof: boolean,
  intl: IntlShape,
): Observable<Required<Context>> {
  const lock$ = containerIsReady$.pipe(filter(Boolean), take(1));
  return combineLatest([getPDFBytes(options.mainPdfUrl), lock$]).pipe(
    switchMap(([pdfBytes]) => {
      if (pdfBytes) {
        return instanceFromOptions(module, pdfBytes, options, containerIsReady$, zoomRef, isProof);
      }
      options.onPermissionError?.();
      return EMPTY;
    }),
    switchMap(({ instance, instanceCallbacks }) => {
      // NOTE: It might seem more efficient to do checkmark attachment and appended pdfs in parallel but pspdfkit
      // can't seem to handle `createAttachment` and `applyOperations` at the same time, so we don't forkJoin.
      // Its also purposeful to put appending pdfs first.
      return appendedPdfsFromInstance(instance, options).pipe(
        switchMap((pageInformationLookup) => {
          return checkMarkAttachmentIdFromInstance(instance).pipe(
            switchMap((checkmarkAttachmentId) => {
              return expiringNotarizeDecorationContext({
                module,
                instance,
                checkmarkAttachmentId,
                pageInformationLookup,
              });
            }),
            tap((decorationContext) => {
              renderCustomOverlays(module, instance, options);
              attachEventListeners(module, instance, pageInformationLookup, intl, {
                onPagePress: options.onPagePress,
                onPageChange: options.onPageChange,
                onSelectedDesignationChange: options.onSelectedDesignationChange,
                onZoom: (zoom) => {
                  zoomRef.current = zoom;
                },
                onAnnotationWillChange: options.onAnnotationWillChange,
              });
              instanceCallbacks.tooltip = (pspAnno) => {
                const notarizeId = getNotarizeIdFromPspId(pspAnno.id);
                const isTextAnnotation = pspAnno instanceof module.Annotations.TextAnnotation;
                const reassignFn = decorationContext.getReassignFn(pspAnno);
                const addToGroupFn = decorationContext.getAddToGroupFn(pspAnno);
                const addConditionalFn = decorationContext.getAddConditionalFn(pspAnno);
                const setOptionalFn = decorationContext.getSetOptionalFn(pspAnno);
                const deleteFn = decorationContext.getDeleteFn(pspAnno);
                const incrementalResizeFn = decorationContext.getIncrementalResizeFn(pspAnno);
                const tooltips = [];
                if (reassignFn) {
                  tooltips.push({
                    type: "custom" as const,
                    title: intl.formatMessage(LABELS.reassign),
                    onPress: reassignFn,
                  });
                }
                if (addToGroupFn) {
                  tooltips.push({
                    type: "custom" as const,
                    title: intl.formatMessage(LABELS.addToGroup),
                    onPress: addToGroupFn,
                  });
                }
                if (addConditionalFn) {
                  tooltips.push({
                    type: "custom" as const,
                    title: intl.formatMessage(LABELS.addConditional),
                    onPress: addConditionalFn,
                  });
                }
                if (setOptionalFn) {
                  tooltips.push({
                    type: "custom" as const,
                    title: intl.formatMessage(LABELS.setAsOptional),
                    onPress: setOptionalFn,
                  });
                }
                if (incrementalResizeFn && isMobile && mwebAnnotationResizingSettings) {
                  const isDisabled = isTextAnnotation
                    ? pspAnno.fontSize <= mwebAnnotationResizingSettings.minFontSize
                    : pspAnno.boundingBox.height <= mwebAnnotationResizingSettings.minHeight;
                  const { increment } = mwebAnnotationResizingSettings;
                  tooltips.push({
                    type: "custom" as const,
                    title: intl.formatMessage(LABELS.decreaseSize),
                    disabled: isDisabled,
                    className: `Icon icon-text Notarize-annotation-tooltip-icon Notarize-decrease-size ${
                      isDisabled ? "Notarize-annotation-tooltip-disabled" : ""
                    }`,
                    onPress: () =>
                      incrementalResizeFn(
                        increment * -1,
                        isTextAnnotation,
                        mwebAnnotationResizingSettings,
                      ),
                  });
                  tooltips.push({
                    type: "custom" as const,
                    className:
                      "Icon icon-text Notarize-annotation-tooltip-icon Notarize-increase-size",
                    title: intl.formatMessage(LABELS.increaseSize),
                    onPress: () =>
                      incrementalResizeFn(
                        increment,
                        isTextAnnotation,
                        mwebAnnotationResizingSettings,
                      ),
                  });
                }
                if (notarizeId && isTextAnnotation && isMobile && mwebAnnotationResizingSettings) {
                  tooltips.push({
                    type: "custom" as const,
                    className:
                      "Icon icon-pencil-line Notarize-annotation-tooltip-icon Notarize-increase-size",
                    title: intl.formatMessage(LABELS.edit),
                    onPress: () => {
                      focusForIOs();
                      decorationContext.setFocused(notarizeId);
                    },
                  });
                }
                if (deleteFn) {
                  const node = document.createElement("button");
                  const getAnnoLabel = (text: string | undefined, subtype?: AnnotationSubtype) => {
                    if (text) {
                      return `${text} `;
                    }
                    switch (subtype) {
                      case AnnotationSubtype.CHECKMARK:
                        return "checkmark ";
                      case AnnotationSubtype.SIGNATURE:
                        return "signature ";
                      case AnnotationSubtype.INITIALS:
                        return "initials ";
                      default:
                        return "";
                    }
                  };
                  const labelText = getAnnoLabel(
                    pspAnno.text,
                    pspAnno.customData?.subtype as AnnotationSubtype | undefined,
                  );
                  node.classList.add("Icon", "icon-delete", "Notarize-annotation-tooltip-icon");
                  node.setAttribute("type", "button");
                  node.setAttribute("title", "Delete");
                  node.setAttribute("aria-label", intl.formatMessage(LABELS.delete, { labelText }));
                  node.addEventListener("keydown", (e) => {
                    if (e.key === "Enter") {
                      deleteFn();
                    }
                  });
                  tooltips.push({
                    type: "custom" as const,
                    className: "Notarize-annotation-tooltip-icon-wrapper",
                    id: "Notarize-delete-tooltip",
                    onPress: deleteFn,
                    node,
                  });
                }
                return tooltips;
              };
              instanceCallbacks.isEditable = decorationContext.isEditable;
              instanceCallbacks.getRenderFn = decorationContext.getRenderFn;
            }),
            map((decorationContext) => {
              return Object.freeze({
                loadInto, // we reuse this since its the same function
                useTogglePageNavigation: makeTogglePageNavigationHook(module.SidebarMode, instance),
                usePreviewLocation: makePreviewLocationHook(module, {
                  instance,
                  pageInformationLookup,
                }),
                zoom: makeZoomCallback(module.ZoomMode, instance),
                jump: makeJumpCallback(module, instance, pageInformationLookup),
                setFocused: decorationContext.setFocused,
                addDesignation: decorationContext.addDesignation,
                addAnnotation: decorationContext.addAnnotation,
                addIndicator: decorationContext.addIndicator,
              });
            }),
          );
        }),
      );
    }),
  );
}

function contextFromKitModule(
  module: KitModule,
  mwebAnnotationResizingSettings: ResizeSettings | null,
  isProof: boolean,
  intl: IntlShape,
): Observable<Context> {
  return new Observable((observer) => {
    const loadRequests$ = new Subject<LoadOptions | null>(); // null represents a "start"
    const loadInto = (options: LoadOptions) => {
      loadRequests$.next(options);
      return () => loadRequests$.next(null);
    };
    const containerIsReady$ = new BehaviorSubject<boolean>(true);
    const zoomRef = { current: module.ZoomMode.FIT_TO_WIDTH };
    return loadRequests$
      .pipe(
        startWith(null),
        tap((options) => {
          if (options) {
            options.onPageChange?.(null);
          }
        }),
        switchMap((options) => {
          return options
            ? fullyLoadedContextFromOptions(
                module,
                options,
                loadInto,
                containerIsReady$,
                zoomRef,
                mwebAnnotationResizingSettings,
                isProof,
                intl,
              )
            : of(
                Object.freeze({
                  loadInto,
                  useTogglePageNavigation: makeTogglePageNavigationHook(module.SidebarMode),
                  usePreviewLocation: makePreviewLocationHook(module),
                }),
              );
        }),
      )
      .subscribe(observer);
  });
}

export function PDFWrapper({ children }: { children: ReactNode }) {
  const intl = useIntl();
  const module = useLazyPSPDFKit();
  const [context, setContext] = useState(DEFAULT_CONTEXT);
  const mwebAnnotationResizingSettings = useFeatureFlag("mobile-annotations-resizing-settings", {
    increment: 10,
    minFontSize: 8,
    minHeight: 10,
  });
  const isProof = useProofRebrand();

  useEffect(() => {
    if (module) {
      // Checking if object is frozen to avoid "Cannot assign to read only property" error after initially setting IGNORE_DOCUMENT_PERMISSIONS to true
      if (!Object.isFrozen(module.Options)) {
        module.Options.IGNORE_DOCUMENT_PERMISSIONS = true;
        module.Options.PDF_JAVASCRIPT = false;
        // when SELECTION_OUTLINE_PADDING is lower, you can tell that the outline does not perfectly match dimensions of designation
        module.Options.SELECTION_OUTLINE_PADDING = () => 4;
        module.Options.SELECTION_STROKE_WIDTH = 3;
        module.Options.MIN_SHAPE_ANNOTATION_SIZE = 8;
      }
      const sub = contextFromKitModule(
        module,
        mwebAnnotationResizingSettings,
        isProof,
        intl,
      ).subscribe(setContext);
      return () => sub.unsubscribe();
    }
  }, [module]);
  return (
    <PDFContext.Provider value={context}>
      {context === DEFAULT_CONTEXT ? null : children}
    </PDFContext.Provider>
  );
}

export function usePDFContext() {
  return useContext(PDFContext);
}

export function useIsPdfLoaded(): boolean {
  return "jump" in usePDFContext();
}
